import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { Send, MessageCircle, Loader2, Menu, Pencil, Ellipsis, Trash2, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { useWebSocket } from '../hooks/WebSocket';
import { useUser } from "../context/UserContext";
import ChatBar from './ChatBar';
import utils from "../utils/Utils";

const ChatBox = ({ chatKey, chatIndex, sessions, refreshAll, setRefreshAll, onDeleteSession }) => {
    const { user, authService } = useUser();
    const [refreshBox, setRefreshBox] = useState(false);
    const [chatBarOpen, setChatBarOpen] = useState(false);
    const [selectedSession, setSelectedSession] = useState([]);
    const [autoReconnect, setAutoReconnect] = useState(true);
    const newChatTitle = "New Chat";
    const [chatTitle, setChatTitle] = useState(newChatTitle);
    const [backTitle, setBackTitle] = useState('');
    const [updateTitle, setUpdateTitle] = useState(false);
    const [inputMessage, setInputMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [chatMenuOpen, setChatMenuOpen] = useState(false);
    const messagesEndRef = useRef(null);
    const titleRef = useRef(null);
    const inputRef = useRef(null);
    
    const userId = user.user_id;
    const { 
        socket,
        connectionStatus,
        currentSessionId,
        messages,
        currentResponse,
        isStreaming,
        followUpSuggestions,
        sessionAnalytics,
        userStats,
        sendMessage,
        stopGeneration,
        getSuggestions,
        performSessionAction,
        connect,
        disconnect,
    } = useWebSocket(userId, selectedSession, autoReconnect, setRefreshAll);

    const urlRegex = /(https?:\/\/[^\s]+?)(?=[.,;:!?)\]}]*(?:\s|$))/g;

    const isImageURL = (url) => {
        const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i;
        return imageExtensions.test(url);
    };

    const extractURLs = (text) => {
        const urls = text.match(urlRegex) || [];
        return [...new Set(urls)];
    };

    const renderTextWithLinks = (text) => {
        const parts = text.split(urlRegex);
        return parts.map((part, index) => {
        if (urlRegex.test(part)) {
            return (
            <a
                key={index}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-400 hover:text-orange-400"
            >
                <Search className="w-4 h-4 inline-block align-bottom hover:scale-130" />
            </a>
            );
        }
        return part;
        });
    };
    
    useEffect(() => {
        // setAutoReconnect(true);

        return () => {
        // console.log("clean up WebSocket autoReconnect");
        setAutoReconnect(false);
        };
    }, []);

    useEffect(() => {
        if (refreshAll) {
        // console.log("refreshAll", chatIndex);
        setRefreshBox(true);
        }
    }, [refreshAll]);

    useEffect(() => {
        if (refreshBox) {
        // console.log("refreshBox", chatIndex);
        setRefreshBox(false);
        getSessions(chatIndex);
        }
    }, [refreshBox]);

    const getSessions = async (chatIndex) => {
        try {
        if (sessions) {
            if (sessions?.length > 0) {
            getSession(sessions[chatIndex]?.id);

            } else {
            if (chatIndex === 0) {
                handleNewSession();
            }
            }
        }

        } catch {
        toast.error("Failed to get sessions");
        }
    };

    useEffect(() => {
        getSessions(chatIndex);
    }, [chatIndex, sessions]);

    useEffect(() => {
        if ( connectionStatus === "Connected" ) {
        // toast.success('Chat Connected');
        if ( currentSessionId ) {
            inputRef.current.focus();
        }
        }
    }, [connectionStatus]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

        if (!updateTitle) {
        inputRef.current.focus();
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, currentResponse]);

    const handleUpdateTitle = (e) => {
        e.preventDefault();

        if (!updateTitle) {
        if (chatTitle === newChatTitle) {
            setChatTitle('');
            setBackTitle('');
        } else {
            setBackTitle(chatTitle);
        }
        setUpdateTitle(true);
        return;
        }

        if (e.key === 'Escape') {
        setChatTitle((backTitle === '') ? newChatTitle : backTitle);
        setBackTitle('');
        setUpdateTitle(false);
        inputRef.current.focus();
        return;
        }

        if (e.key === 'Enter') {
        if (!currentSessionId) {
            if (chatTitle === '') {
            setChatTitle(newChatTitle);
            }
            setBackTitle('');
            setUpdateTitle(false);
            inputRef.current.focus();
            return;
        }

        const putTitle = async (sessionId) => {
            let thisTitle = chatTitle;
            if (thisTitle === '') {
            if (backTitle === '') {
                thisTitle = newChatTitle;
            } else {
                thisTitle = backTitle;
            }
            }

            try {
            // const response = await axios.put(`${config.apiBaseUrl}/chat/session/${sessionId}`, {
            const response = await authService.put(`/chat/session/${sessionId}`, {
                user_id: user.user_id,
                title: thisTitle,
            });
            if (response.status === 200) {
                toast.success("Chat title updated");
                if (thisTitle !== chatTitle) {
                setChatTitle(thisTitle);
                }
                setBackTitle('');
                setUpdateTitle(false);
            }

            } catch {
            toast.error("Failed to update chat title");
            }
        };

        putTitle(currentSessionId);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!inputMessage.trim() || isStreaming) return;

        setIsSending(true);
        const success = sendMessage(inputMessage, currentSessionId, chatTitle);
        if (success) {
        setInputMessage('');

        setTimeout(() => {
            setIsSending(false);
            setRefreshAll(true);
        }, 3000);
        }
    };

    // useEffect(() => {
    //   if (isSending) {
    //     if (!isStreaming) {
    //       // console.log('Streaming done');

    //       setTimeout(() => {
    //         // console.log('Refresing');
    //         setIsSending(false);
    //         // getSessions(chatIndex);
    //         // setRefreshAll(true);
    //       }, 3000);
    //     }
    //   }
    // }, [isSending, isStreaming]);

    const getSession = async (sessionId) => {
        if ( currentSessionId === sessionId ) return;

        if ( !sessionId ) {
        setChatTitle(newChatTitle);
        setSelectedSession([]);
        return;
        }

        try {
        // const response = await axios.get(`${config.apiBaseUrl}/chat/session/${sessionId}`);
        const response = await authService.get(`/chat/session/${sessionId}`);
        // console.log(response?.data);

        const session = response?.data;
        setChatTitle(session?.title);
        setSelectedSession(response?.data);
        setUpdateTitle(false);

        } catch {
        // toast.error("Failed to get session details");
        };
    };

    const handleSessionSelect = (sessionId) => {
        getSession(sessionId);
    };

    const handleNewSession = () => {
        setSelectedSession([]);
        setChatTitle('');
        setBackTitle('');
        setUpdateTitle(true);
    };

    useEffect(() => {
        if (updateTitle || !chatBarOpen) {
        titleRef?.current?.focus();
        }
        if (chatBarOpen) {
        setUpdateTitle(false);
        }  
    }, [updateTitle, chatBarOpen]);

    const handleChatMenu = () => {
        setChatMenuOpen(!chatMenuOpen);
    };

    const handleChatDelete = () => {
        const deleteSession = async () => {
        try {
            // const response = await axios.delete(`${config.apiBaseUrl}/chat/session/${userId}-${currentSessionId}`);
            const response = await authService.delete(`/chat/session/${userId}-${currentSessionId}`);
            if (response.status === 200) {
            toast.success("Chat deleted successfully");
            setSelectedSession([]);
            setChatTitle(newChatTitle);
            onDeleteSession(chatIndex);
            }

        } catch {
            toast.error("Failed to delete this chat");
        }
        };

        setChatMenuOpen(false);
        deleteSession();
    };

    const truncateText = (str, maxLength) => {
        if (str.length <= maxLength) {
        return str;
        }
        return str.slice(0, maxLength) + "...";
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    return (
        <div className="relative min-h-[calc(66vh)] h-100 w-96 flex flex-col bg-gray-800 mx-4 rounded-lg">
            <ChatBar
                currentSessionId={currentSessionId}
                sessions={sessions}
                onSessionSelect={handleSessionSelect}
                onNewSession={handleNewSession}
                onDeleteSession={() => {}}
                onRenameSession={() => {}}
                isOpen={chatBarOpen}
                onClose={() => setChatBarOpen(false)}
            />

            {/* Header */}
            <div className="bg-gray-800 shadow-sm border-b px-6 py-4 rounded-t-lg">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={() => setChatBarOpen(true)}
                            className=" text-orange-400"
                        >
                            <Menu className="w-6 h-6 hover:scale-120" />
                        </button>
                        <MessageCircle className="w-6 h-6 text-orange-300" />

                        { !updateTitle ?
                        <>
                            <h1 className="my-0.5 text-l font-semibold text-orange-300">{truncateText(chatTitle, 16)}</h1>
                            <button
                                onClick={handleUpdateTitle}
                                className="text-orange-400"
                            >
                                <Pencil className="w-4 h-4 inline-block align-middle hover:scale-120" />
                            </button>
                        </>
                        :
                        <>
                            <input
                                ref={titleRef}
                                type="text"
                                key={`chatTitle-${chatKey}`}
                                value={chatTitle}
                                placeholder={newChatTitle}
                                onChange={(e) => setChatTitle(utils.toTitleCase(e.target.value))}
                                disabled={false}
                                onKeyDown={(e) => (e.key === 'Escape' || e.key === 'Enter') && handleUpdateTitle(e)}
                                // autoFocus={true}
                                className="w-full px-2 py-0 mb-0.5 border rounded-md bg-gray-700 text-gray-200 focus:ring-1 focus:ring-orange-300 focus:border-transparent"
                            />
                        </>
                        }
                    </div>
                    <div className="flex items-center space-x-2">
                        {chatMenuOpen ?
                            <button
                                onClick={handleChatDelete}
                                className="text-orange-400 pr-1"
                            >
                                <Trash2 className="w-4 h-4 mt-1 inline-block align-middle hover:scale-120" />
                            </button>
                        : ''}

                        <button
                            onClick={handleChatMenu}
                            onKeyDown={(e) => (e.key === 'Escape') && setChatMenuOpen(false)}
                            className="text-orange-400 rounded-lg"
                        >
                            <Ellipsis className="w-4 h-4 inline-block align-bottom hover:scale-120" />
                        </button>
                        <div className={`w-2 h-2 rounded-full mt-1.5 ${
                            connectionStatus === 'Connected' ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        {/* <span className="text-sm text-orange-300">{connectionStatus}</span> */}
                    </div>
                </div>
            </div>

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                {messages.length === 0 && !isStreaming && (
                    <div className="text-center bg-gray-800 text-gray-200 mt-12">
                        <MessageCircle className="w-12 h-12 mx-auto mb-4 text-orange-200" />
                        <p className="text-lg text-orange-300">Start a conversation</p>
                        <p className="text-sm text-orange-300">Ask me anything!</p>
                    </div>
                )}

                {messages.map((message, index) => (
                    <div
                        key={index}
                        className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                        <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg shadow-sm ${
                                message.type === 'user'
                                ? 'bg-orange-400 text-white'
                                : message.type === 'error'
                                ? 'bg-red-200 text-red-600'
                                : 'bg-gray-700 text-gray-200 border'
                            }`}
                        >
                            <p className="text-sm whitespace-pre-wrap">{message.type === 'user' ? message.content : renderTextWithLinks(message.content)}</p>
                            <p className={`text-xs mt-1 ${
                                    message.type === 'user' ? 'text-gray-200' : 'text-gray-400'
                                }`}>
                                {formatTimestamp(message.timestamp)}
                            </p>
                        </div>
                    </div>
                ))}

                {/* Streaming Response */}
                {isStreaming && (
                    <div className="flex justify-start">
                        <div className="max-w-xs lg:max-w-md px-4 py-2 rounded-lg shadow-sm bg-gray-700 text-gray-200 border">
                            <p className="text-sm whitespace-pre-wrap">
                                {currentResponse}
                                <span className="inline-block w-2 h-4 bg-gray-700 text-gray-200 ml-1 animate-pulse"></span>
                            </p>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input Form */}
            <div className="bg-gray-800 border-t px-6 py-4 rounded-b-lg">
                <div className="flex space-x-3 rounded-b-lg">
                    <div className="flex-1">
                        <input
                            ref={inputRef}
                            type="text"
                            key={`inputMessage-${chatKey}`}
                            value={inputMessage}
                            placeholder="Type your message..."
                            onChange={(e) => setInputMessage(e.target.value)}
                            disabled={isStreaming || isSending || connectionStatus !== 'Connected'}
                            onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                            // autoFocus={true}
                            className="w-full px-4 py-2 border rounded-lg bg-gray-700 text-gray-200 focus:ring-2 focus:ring-orange-300 focus:border-transparent disabled:bg-gray-500 disabled:cursor-not-allowed"
                        />
                    </div>
                    <button
                        onClick={handleSubmit}
                        disabled={!inputMessage.trim() || isStreaming || connectionStatus !== 'Connected'}
                        className="px-4 py-2 bg-orange-400 text-white rounded-lg duration-300 hover:scale-105 focus:ring-2 disabled:bg-orange-300 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                        {isStreaming || isSending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                        <Send className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">Send</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatBox;